/* eslint-disable class-methods-use-this */

// Third-party
import React, { PureComponent } from 'react';
import classNames from 'classnames';
import merge from 'lodash/merge';
import moment from 'moment';

// Namespaces
import * as ProposalNS from '../../Proposal';
import * as ProposalElementNS from '../../ProposalElements';

// Types
import Dictionary from '@/types/dictionary';
import Nullable from '@/types/nullable';
import ProposalToolbarOption from '@/types/proposal-toolbar-option';
import ProposalTextAreaData from '@/types/proposal-text-area-data';
import ProposalFieldData from '@/types/proposal-field-data';
import ProposalTableProps from '@/types/proposal-table';

// Utils
import {
  ProposalDAOInterfaces,
  ProposalDAOInterface,
  DisclaimerConfig,
  ProposalItemFieldsDAOInterface,
  ProposalTableDataDAOInterface,
  ProposalDataDAOInterface,
  ProposalTextsDAOInterface,
  ProposalTextAreaDAOInterface,
  ProposalToolbarOptionDAOInterface,
  ProposalScatterChartOptionsDAOInterface,
  ComparisonItemBasedProposalDAOInterface,
} from '../../DAOs/ProposalDAO';
import { DAOManager } from '../../DAOs/DAOManager';
import { formatLocalizedString } from '@/utils/i18n';
import { PROPOSAL_DISCLAIMERS, PROPOSAL_ORIENTATION } from '../../utils/constants';
import { PERMISSIONS, userHasPermissionEnabled } from '@/utils/permissions';
import { TIME_PERIODS } from '@/utils/constants';
import { monthsToPeriodFromValues } from '@/utils/charts';
import { monthsToPeriod } from '@/utils/time';
import { getDateQuickRangeFromToolbarConfig } from '@/utils/time-range-presets';
import { findBestMatch } from '@/utils/strings';
import { DISCLAIMER_FOLLOWING_RELEVANCE_PERIOD } from '@/utils/disclaimers/constants';
import {
  buildAltText,
  buildTitle,
  buildScatterAltText,
  buildScatterTitle,
  buildDonutAltText,
  buildDonutTitle,
  buildBarAltText,
  buildBarTitle,
  getLocaleEnum,
} from '@/utils/a11y/chart-alt-text/chart-text-builders';
import { formatters } from '@/utils/a11y/chart-alt-text/chart-formatters';
import { getSampledSeriesForAlt, getSampledSeriesForScatter } from '@/utils/a11y/chart-alt-text/chart-samplers';
import { FEATURE_FLAGS } from '@/enums/FeatureFlags';
import { withFeatureFlags, WithFeatureFlagsProps } from '@/hocs/withFeatureFlags';
import { getSectionDisplayTitle } from '@/modules/proposal/utils/section-titles';

// Components
import { ProposalFieldDAO } from '../../DAOs/ProposalFieldDAO';
import Grid, { GridProps } from '../layout/Grid';
import ProposalBarChart, { BarChartProps } from '../views/ProposalBarChart';
import ProposalDisclaimer from '../misc/ProposalDisclaimer';
import ProposalDonut, { ProposalDonutProps } from '../views/ProposalDonut';
import ProposalField, { ProposalFieldProps } from '../layout/ProposalField';
import ProposalHTML, { ProposalHTMLProps } from '../views/ProposalHTML';
import ProposalLineChart, { LineChartProps } from '../views/ProposalLineChart';
import ProposalScatterPlot, { ScatterPlotProps } from '../views/ProposalScatterPlot';
import ProposalTable from '../views/proposal-table/ProposalTable';
import ProposalText, { ProposalTextProps } from '../views/ProposalText';
import ProposalTextArea, { ProposalTextAreaProps } from '../views/ProposalTextArea';
import ProposalToolbar, { ProposalToolbarProps } from '../views/ProposalToolbar';
import ProposalViewControl, { ProposalViewControlProps } from '../views/ProposalViewControl';
import Card, { CardProps } from '../layout/Card';
import { Typography } from '@capintel/component-lib/typography';
import { ALLOWED_HTML_TAGS } from '@/views/components/i18n/FormattedMessageV2';

type Props = {
  proposal: ProposalNS.Proposal;
  section: ProposalElementNS.ProposalSectionElement;
  selectedViews: Dictionary<string>;
  hiddenLayouts: string[];
  pageNumber: number;
  numberOfPages: number;
  setToolbarOption: (key: string, toolbarOption: ProposalToolbarOption) => void;
  setTextAreaData: (key: string, textAreaData: ProposalTextAreaData) => void;
  setSelectedView: (key: string, value: string) => void;
} & WithFeatureFlagsProps;

const generateChartAltText = (titleText: string, altText: string) => {
  const combined = `${titleText} ${altText}`;
  return { 'en-US': combined, 'en-CA': combined, 'fr-CA': combined };
};

class ProposalSectionComponent extends PureComponent<Props> {
  onViewChanged(value: any) {
    this.props.setSelectedView(this.props.section.key, value);
  }

  /**
   * Extracts dateQuickRange from a toolbar option
   */
  private extractDateQuickRangeFromToolbar = (toolbarOption: ProposalToolbarOption, toolbarConfig?: ProposalNS.ToolbarOptionConfig): Nullable<TIME_PERIODS> => {
    const { proposal } = this.props;

    if (toolbarOption.period) {
      return toolbarOption.period;
    }

    if (toolbarOption.monthsRange) {
      return monthsToPeriod(toolbarOption.monthsRange, proposal.monthEndDataOption);
    }

    if (toolbarOption.startDate && toolbarOption.endDate && toolbarConfig) {
      const months = moment(toolbarOption.endDate).diff(toolbarOption.startDate, 'months');
      const dateQuickRangeValues = getDateQuickRangeFromToolbarConfig(toolbarConfig, proposal);
      return monthsToPeriodFromValues(months, toolbarOption.monthsRange, dateQuickRangeValues);
    }

    return null;
  };

  /**
   * Checks if section has DateQuickRange in toolbar elements
   */
  private hasDateQuickRangeInToolbar = (section: ProposalElementNS.ProposalSectionElement): boolean =>
    section.toolbar?.toolbarElements?.includes(ProposalNS.ToolbarElement.DateQuickRange) ?? false;

  /**
   * Gets toolbar option by specific toolbar key from section
   */
  private getToolbarOptionByKey = (section: ProposalElementNS.ProposalSectionElement): Nullable<ProposalToolbarOption> => {
    const { proposal } = this.props;
    const toolbarKey = section.toolbar?.key;

    if (!toolbarKey || !proposal.toolbarOptions?.[toolbarKey] || !this.hasDateQuickRangeInToolbar(section)) {
      return null;
    }

    const toolbarOption = proposal.toolbarOptions[toolbarKey];
    return toolbarOption;
  };

  /**
   * Finds toolbar key that matches section key patterns
   */
  private findMatchingSectionKey = (section: ProposalElementNS.ProposalSectionElement): Nullable<string> => {
    const { proposal } = this.props;

    const toolbarKeys = Object.keys(proposal.toolbarOptions || {});
    const sectionKeys = [section.sectionKey, section.key].filter(Boolean);

    return findBestMatch(toolbarKeys, sectionKeys);
  };

  /**
   * Gets toolbar option by matching section key patterns
   */
  private getToolbarOptionBySectionKey = (section: ProposalElementNS.ProposalSectionElement): Nullable<ProposalToolbarOption> => {
    const { proposal } = this.props;
    const matchingKey = this.findMatchingSectionKey(section);

    if (!matchingKey || !proposal.toolbarOptions) {
      return null;
    }

    const toolbarOption = proposal.toolbarOptions[matchingKey];
    return toolbarOption;
  };

  /**
   * Extracts dateQuickRange from toolbar options for disclaimers
   */
  private extractToolbarOptionForDisclaimer = (section: ProposalElementNS.ProposalSectionElement): Nullable<ProposalToolbarOption> =>
    this.getToolbarOptionByKey(section) || this.getToolbarOptionBySectionKey(section);

  /**
   * Extracts complete date range configuration (startDate, endDate, dateQuickRange) from toolbar options for disclaimers
   */
  private extractDateRangeConfigForDisclaimer = (section: ProposalElementNS.ProposalSectionElement) => {
    const toolbarOption = this.extractToolbarOptionForDisclaimer(section);
    const dateQuickRange = toolbarOption ? this.extractDateQuickRangeFromToolbar(toolbarOption, section.toolbar) : null;

    return {
      startDate: toolbarOption?.startDate || null,
      endDate: toolbarOption?.endDate || null,
      dateQuickRange,
    };
  };

  renderToolbar(element: ProposalElementNS.ProposalSectionElement): Nullable<JSX.Element> {
    const { proposal } = this.props;

    if (element.toolbar !== undefined) {
      const dataElement = {
        type: ProposalNS.ProposalTemplateElementType.Data,
        elementType: ProposalElementNS.ProposalElementType.Data,
        parent: element.layout,
        dataView: {
          type: ProposalNS.DataView.ChartToolbar,
          className: element.toolbar.className,
        },
        DAO: {
          type: ProposalNS.DAOType.Toolbar,
        },
        toolbarOptionConfig: element.toolbar,
      } as ProposalElementNS.ProposalDataElement;
      const dao: ProposalDAOInterface = DAOManager.getInstance().buildDAO(ProposalNS.DAOType.Toolbar, this.props.proposal, dataElement);
      dataElement.daoObject = dao;

      let toolBarPeriod;
      if (proposal.toolbarOptions !== undefined && proposal.toolbarOptions[element.toolbar.key] !== undefined) {
        const toolbarOption: ProposalToolbarOption = proposal.toolbarOptions[element.toolbar.key];
        if (!element.toolbar.hidePeriodLabel && toolbarOption.periodLabel !== undefined) {
          toolBarPeriod = `(${formatLocalizedString(toolbarOption.periodLabel, getLocaleEnum(proposal.options.locale))})`;
        }
      }

      return (
        <>
          {toolBarPeriod !== undefined && <span className="selected-period print-only">{toolBarPeriod}</span>}
          {this.renderDataElement(dataElement, 0, element.sectionIteratorIndex)}
        </>
      );
    }

    return null;
  }

  renderElements(elements: ProposalElementNS.ProposalElement[], sectionIteratorIndex?: number): Array<Nullable<JSX.Element>> {
    return elements
      .map((element, index) => {
        switch (element.type) {
          case ProposalNS.ProposalTemplateElementType.Layout: {
            return this.renderLayoutElement(element, index, sectionIteratorIndex);
          }
          case ProposalNS.ProposalTemplateElementType.Data: {
            return this.renderDataElement(element, index, sectionIteratorIndex);
          }
          default:
            return null;
        }
      })
      .filter(elem => elem !== null);
  }

  renderSectionElement(element: ProposalElementNS.ProposalSectionElement) {
    const { proposal } = this.props;
    const { layout, key, subtitle, showTitle = true, showTitleOnPageOverflow = false } = element;
    const { pageOverflowIndex } = element;
    const showSectionTitle: boolean = showTitle && (pageOverflowIndex !== 0 && showTitleOnPageOverflow !== undefined ? showTitleOnPageOverflow : true);
    const sectionTitle = getSectionDisplayTitle(element, proposal);

    return (
      <React.Fragment key={key}>
        {showSectionTitle && (
          <div className="proposal-section-header d-flex align-items-center">
            <div className="flex-fill proposal-section-header-content">
              <Typography as={ALLOWED_HTML_TAGS.H2} color="primary" variant="header2Xs">
                {sectionTitle}
                {subtitle && <small> {formatLocalizedString(subtitle, getLocaleEnum(proposal.options.locale))}</small>}
              </Typography>
            </div>
            {this.renderToolbar(element)}
          </div>
        )}

        {this.renderLayoutElement(layout as ProposalElementNS.ProposalLayoutElement, -1, element.sectionIteratorIndex)}
      </React.Fragment>
    );
  }

  renderLayoutElement(element: ProposalElementNS.ProposalLayoutElement, index: number, sectionIteratorIndex?: number) {
    const {
      elements,
      layout: { type: layoutType, columns, autoCol, rows },
      key,
    } = element;

    if (key) {
      const { hiddenLayouts } = this.props;
      if (hiddenLayouts?.includes(key)) {
        return null;
      }
    }

    switch (layoutType) {
      case ProposalNS.LayoutType.Grid: {
        const gridElements = this.renderElements(elements as ProposalElementNS.ProposalElement[], sectionIteratorIndex);

        return React.createElement<GridProps>(Grid, {
          key: index,
          columns,
          autoCol,
          rows,
          elements: gridElements,
          className: `${element.layout.className || ''} items-${gridElements.length}`,
        });
      }
      case ProposalNS.LayoutType.Card: {
        const cardElements = this.renderElements(elements as ProposalElementNS.ProposalElement[], sectionIteratorIndex);

        return React.createElement<CardProps>(Card, {
          key: index,
          elements: cardElements,
          className: `${element.layout.className || ''} items-${cardElements.length}`,
        });
      }
      default:
        return null;
    }
  }

  renderDataElement = (element: ProposalElementNS.ProposalDataElement, index: number, sectionIteratorIndex?: number) => {
    let { dataView } = element;
    const { numberOfPages, pageNumber, setToolbarOption, setTextAreaData, setSelectedView, selectedViews } = this.props;
    const { proposal } = this.props;
    const { featureFlags } = this.props;

    const isPDFChartAutoAltTextEnabled = featureFlags.isFFEnabled(FEATURE_FLAGS.PDF_CHART_AUTO_ALT_TEXT_ENABLED);

    const dao: ProposalDAOInterface | undefined = element.daoObject;

    if (!dao) {
      return null;
    }

    const toolbarOptionInterface: ProposalToolbarOptionDAOInterface | undefined = dao.getInterface<ProposalToolbarOptionDAOInterface>(
      ProposalDAOInterfaces.ToolbarOption,
    );

    switch (dataView.type) {
      case ProposalNS.DataView.Text: {
        const localDAO: ProposalTextsDAOInterface | undefined = dao.getInterface<ProposalTextsDAOInterface>(ProposalDAOInterfaces.Texts);
        if (localDAO === undefined) {
          return null;
        }

        return React.createElement<ProposalTextProps>(ProposalText, {
          key: index,
          texts: localDAO.getTexts({ sectionIteratorIndex }),
          className: dataView.className,
        });
      }
      case ProposalNS.DataView.Html: {
        const localDAO: ProposalTextsDAOInterface | undefined = dao.getInterface<ProposalTextsDAOInterface>(ProposalDAOInterfaces.Texts);
        if (localDAO === undefined) {
          return null;
        }

        return React.createElement<ProposalHTMLProps>(ProposalHTML, {
          key: index,
          texts: localDAO.getTexts({
            pageOverflowIndex: element.pageOverflowIndex,
            pageNumber,
            itemIndex: index,
            numberOfPages,
            sectionIteratorIndex,
          }),
          className: dataView.className,
          proposal,
          locale: proposal.options.locale,
          accessibilityDescription: dataView.accessibilityDescription,
          setTextAreaData, // now needed to `inject` textarea in html blocks
        });
      }
      case ProposalNS.DataView.Table: {
        const { orientation } = proposal.options;
        const maxRows = (dataView.dynamicMaxRowsPerPage ? dataView.dynamicMaxRowsPerPage[orientation] : undefined) ?? dataView.maxRowsPerPage;
        const skip = maxRows ? element.pageOverflowIndex * maxRows : undefined;
        const limit = maxRows;

        const localDAO: (ProposalTableDataDAOInterface & ComparisonItemBasedProposalDAOInterface) | undefined = dao.getInterface<
          ProposalTableDataDAOInterface & ComparisonItemBasedProposalDAOInterface
        >(ProposalDAOInterfaces.TableData);

        if (localDAO === undefined) {
          return null;
        }

        const dataViewClassname = dataView.className || '';
        const proposalItems = localDAO.getItemDAOs().map(({ item }) => item);

        return React.createElement<ProposalTableProps>(ProposalTable, {
          key: index,
          tableData: localDAO.getTableData(skip, limit),
          className: `${localDAO.getTableClassName()} ${element.DAO.type} ${element.dataView.type} ${dataViewClassname}`,
          tableOption: {
            showHeader: dataView.showHeader,
            orientation: localDAO.tableOrientationOptions?.orientation,
          },
          locale: proposal.options.locale,
          accessibilityDescription: dataView.accessibilityDescription,
          toolbarOption: toolbarOptionInterface?.getToolbarOption(),
          proposalItems,
          user: proposal.appUser,
        });
      }
      case ProposalNS.DataView.Grid: {
        dataView = dataView as ProposalNS.GridViewOptions;

        const localDAO: ProposalItemFieldsDAOInterface | undefined = dao.getInterface<ProposalItemFieldsDAOInterface>(ProposalDAOInterfaces.ItemFields);
        if (localDAO === undefined) {
          return null;
        }
        const fields: Array<ProposalFieldDAO> = localDAO.getItemFields();

        const pfs: JSX.Element[] = fields.map(field => {
          const fieldData: ProposalFieldData = {
            label: field.label,
            value: field.value,
            cellType: field.cellType,
            cellFormat: field.cellFormat,
            withSuffixes: field.withSuffixes,
            color: field.color,
            itemType: field.itemType,
            flags: field.flags,
            currency: field.currency,
            decimalPlace: field.decimalPlace,
          };

          return React.createElement<ProposalFieldProps>(ProposalField, {
            key: field.label,
            field: fieldData,
            locale: proposal.options.locale,
            showBaseCurrencies: proposal.showBaseCurrencies,
          });
        });

        return React.createElement<GridProps>(Grid, {
          key: index,
          elements: pfs,
          columns: dataView.columns,
          className: `${dataView.className || ''} items-${pfs.length}`,
        });
      }
      case ProposalNS.DataView.Donut: {
        const localDAO: ProposalDataDAOInterface | undefined = dao.getInterface<ProposalDataDAOInterface>(ProposalDAOInterfaces.Data);
        if (localDAO === undefined) {
          return null;
        }

        const donutData = localDAO.getDonutData(0);
        if (!donutData) {
          return null;
        }

        let altDescription = dataView.accessibilityDescription;

        if (isPDFChartAutoAltTextEnabled) {
          const hasValidData =
            donutData.data && Array.isArray(donutData.data) && donutData.data.length > 0 && Array.isArray(donutData.data[0]) && donutData.data[0].length > 0;

          if (hasValidData) {
            const titleText = buildDonutTitle(donutData, proposal.options.locale);
            const altText = buildDonutAltText(donutData, proposal.options.locale);

            altDescription = generateChartAltText(titleText, altText);
          }
        }

        return React.createElement<ProposalDonutProps>(ProposalDonut, {
          key: index,
          donutData,
          className: dataView.className,
          configOptions: dataView.configOptions,
          showHeader: dataView.showHeader,
          showLegend: dataView.showLegend,
          showTotal: dataView.showTotal,
          showCurrency: proposal.showBaseCurrencies,
          colorsDict: donutData.colorsDict,
          locale: proposal.options.locale,
          accessibilityDescription: altDescription,
        });
      }
      case ProposalNS.DataView.Line: {
        const localDAO: ProposalDataDAOInterface | undefined = dao.getInterface<ProposalDataDAOInterface>(ProposalDAOInterfaces.Data);
        if (localDAO === undefined) {
          return null;
        }

        const toolbarOption = toolbarOptionInterface?.getToolbarOption();
        let altDescription = dataView.accessibilityDescription;
        const additionalConfigOptions = localDAO.getGraphConfigOptions && localDAO.getGraphConfigOptions();

        if (isPDFChartAutoAltTextEnabled) {
          const parentLabel = localDAO.proposalElement?.parent?.parent?.label?.['en-CA'];
          const isHistoricalScenario = typeof parentLabel === 'string' && parentLabel.toLowerCase().includes('historical');

          let scenarioName: string | null = null;
          const scenarios = localDAO.proposal?.selectedHistoricalScenarios;

          if (Array.isArray(scenarios) && scenarios.length > 0 && isHistoricalScenario) {
            const scenarioIndex = localDAO.sectionIteratorIndex || 0;
            if (scenarios[scenarioIndex]) {
              const scenario = scenarios[scenarioIndex];
              scenarioName = scenario?.name?.[proposal.options.locale]?.trim() || null;
            }
          }

          const series = localDAO.getLineSeries();
          const sampledSeries = getSampledSeriesForAlt(series, toolbarOption?.useMonthly ?? false, localDAO.proposal?.options.locale);

          let scenarioPeriod: { startDate: string; endDate: string } | undefined;
          if (isHistoricalScenario && additionalConfigOptions?.xAxis?.plotBands?.length > 0) {
            const plotBand = additionalConfigOptions.xAxis.plotBands[0];
            if (plotBand.from && plotBand.to) {
              const startDate = formatters.date(plotBand.from, proposal.options.locale, false);
              const endDate = formatters.date(plotBand.to, proposal.options.locale, false);
              scenarioPeriod = { startDate, endDate };
            }
          }

          const titleText = buildTitle(sampledSeries, proposal.options.locale, 'line', isHistoricalScenario ? scenarioName : undefined, scenarioPeriod);
          const altText = buildAltText(sampledSeries, proposal.options.locale, proposal.items);

          altDescription = generateChartAltText(titleText, altText);
        }
        return React.createElement<LineChartProps>(ProposalLineChart, {
          key: index,
          data: localDAO.getLineSeries(),
          configOptions: merge({}, dataView.configOptions, additionalConfigOptions),
          className: dataView.className,
          locale: proposal.options.locale,
          accessibilityDescription: altDescription,
          toolbarOption,
        });
      }
      case ProposalNS.DataView.Scatter: {
        const localDAO: ProposalScatterChartOptionsDAOInterface | undefined = dao.getInterface<ProposalScatterChartOptionsDAOInterface>(
          ProposalDAOInterfaces.ScatterChartOptions,
        );
        if (localDAO === undefined) {
          return null;
        }

        let altDescription = dataView.accessibilityDescription;
        const scatterData = localDAO.data;

        if (isPDFChartAutoAltTextEnabled) {
          const periodLabel = localDAO.toolbarOption?.periodLabel
            ? formatLocalizedString(localDAO.toolbarOption.periodLabel, getLocaleEnum(proposal.options.locale))
            : null;

          const sampledSeries = getSampledSeriesForScatter(scatterData || [], false);
          const titleText = buildScatterTitle(sampledSeries, proposal.options.locale, periodLabel);
          const altText = buildScatterAltText(sampledSeries, proposal.options.locale);

          altDescription = generateChartAltText(titleText, altText);
        }

        return React.createElement<ScatterPlotProps>(ProposalScatterPlot, {
          key: index,
          data: scatterData,
          configOptions: dataView.configOptions,
          daoChartOptions: localDAO.getScatterChartOptions(),
          className: dataView.className,
          locale: proposal.options.locale,
          accessibilityDescription: altDescription,
          toolbarOption: toolbarOptionInterface?.getToolbarOption(),
        });
      }
      case ProposalNS.DataView.Bars: {
        const localDAO: (ProposalDataDAOInterface & ComparisonItemBasedProposalDAOInterface) | undefined = dao.getInterface<
          ProposalDataDAOInterface & ComparisonItemBasedProposalDAOInterface
        >(ProposalDAOInterfaces.Data);
        if (localDAO === undefined) {
          return null;
        }
        const data = localDAO.getSeriesData();
        const proposalItems = localDAO.getItemDAOs().map(({ item }) => item);

        let altDescription = dataView.accessibilityDescription;

        if (isPDFChartAutoAltTextEnabled) {
          const hasValidData = data && Array.isArray(data) && data.length > 0 && data.some(series => Array.isArray(series) && series.length > 0);

          if (hasValidData) {
            const titleText = buildBarTitle(data, proposalItems, proposal.options.locale);
            const altText = buildBarAltText(data, proposalItems, proposal.options.locale);

            altDescription = generateChartAltText(titleText, altText);
          }
        }
        return React.createElement<BarChartProps>(ProposalBarChart, {
          data,
          showHeader: dataView.showHeader,
          className: dataView.className,
          valueFormatter: dataView.valueFormatter,
          configOptions: dataView.configOptions,
          colorsDict: dao.getColorsDict(),
          locale: proposal.options.locale,
          proposalItems,
          showBaseCurrency: proposal.showBaseCurrencies,
          accessibilityDescription: altDescription,
        });
      }
      case ProposalNS.DataView.ChartToolbar: {
        const localDAO: ProposalToolbarOptionDAOInterface | undefined = dao.getInterface<ProposalToolbarOptionDAOInterface>(
          ProposalDAOInterfaces.ToolbarOption,
        );
        if (localDAO === undefined) {
          return null;
        }
        const toolbarOption = localDAO.getToolbarOption();
        return React.createElement<ProposalToolbarProps>(ProposalToolbar, {
          setToolbarOption,
          proposal,
          toolbarOptionConfig: element.toolbarOptionConfig,
          toolbarOption,
          className: dataView.className,
          sectionIteratorIndex,
        });
      }
      case ProposalNS.DataView.ViewControl: {
        return React.createElement<ProposalViewControlProps>(ProposalViewControl, {
          proposal,
          viewKey: dataView.key,
          className: dataView.className,
          availableViews: dataView.availableViews,
          selectedViews,
          setSelectedView,
        });
      }
      case ProposalNS.DataView.TextArea: {
        const localDAO: ProposalTextAreaDAOInterface | undefined = dao.getInterface<ProposalTextAreaDAOInterface>(ProposalDAOInterfaces.TextArea);
        if (localDAO === undefined) {
          return null;
        }

        return React.createElement<ProposalTextAreaProps>(ProposalTextArea, {
          setTextAreaData,
          proposal,
          initialValue: localDAO.getTextAreaInitialValue(element.textAreaOptionConfig && element.textAreaOptionConfig.default),
          textAreaOptionConfig: element.textAreaOptionConfig,
          className: dataView.className,
        });
      }
      default:
        return null;
    }
  };

  renderDisclaimer(
    disclaimerConfig: DisclaimerConfig,
    showItemNames: boolean | undefined,
    i: number,
    detailed: boolean | undefined,
    hideSpecificDisclaimerKeys: string[] | undefined,
    section: ProposalElementNS.ProposalSectionElement,
  ) {
    const { proposal } = this.props;

    // Specific case if a type of disclaimer needs to be hidden for a section
    if (hideSpecificDisclaimerKeys?.includes(disclaimerConfig.key)) {
      return null;
    }

    const dateRangeConfig = this.extractDateRangeConfigForDisclaimer(section);

    const applyRelevanceYearLimit = DISCLAIMER_FOLLOWING_RELEVANCE_PERIOD.includes(section.sectionKey);

    return (
      <ProposalDisclaimer
        proposal={proposal}
        key={`${disclaimerConfig.key}-${i}`}
        disclaimerKey={disclaimerConfig.key}
        items={disclaimerConfig.items}
        locale={proposal.options.locale}
        config={disclaimerConfig.config}
        showItemNames={showItemNames}
        detailed={detailed}
        dateRangeConfig={dateRangeConfig}
        applyRelevanceYearLimit={applyRelevanceYearLimit}
        sectionKey={section.key}
      />
    );
  }

  hasSinceInceptionDisclaimers(section: ProposalElementNS.ProposalSectionElement) {
    const {
      proposal: { appUser },
    } = this.props;
    const { sectionKey } = section;
    const {
      DAOType: { singlePageFund, singlePagePortfolio, singlePageGroup },
    } = ProposalNS;
    const isSinglePage = sectionKey === singlePageFund || sectionKey === singlePagePortfolio || sectionKey === singlePageGroup;
    const hasPermission = userHasPermissionEnabled(appUser, PERMISSIONS.COMMON_INCEPTION);
    return isSinglePage && hasPermission;
  }

  filterDisclaimerConfigs(configs: DisclaimerConfig[]) {
    const { SINCE_INCEPTION_DATE } = PROPOSAL_DISCLAIMERS;
    return configs.reduce((acc: DisclaimerConfig[], curr: DisclaimerConfig) => {
      const hasSinceInception = acc.some(({ key }) => key === SINCE_INCEPTION_DATE) && curr.key === SINCE_INCEPTION_DATE;
      if (!hasSinceInception && curr) {
        acc.push(curr);
      }
      return acc;
    }, [] as DisclaimerConfig[]);
  }

  render() {
    const { section, proposal } = this.props;

    const isLandscapeOrientation = proposal.options.orientation === PROPOSAL_ORIENTATION.LANDSCAPE;

    if (section && section.type === ProposalNS.ProposalTemplateElementType.Section) {
      return (
        <div
          className={classNames(`proposal-preview-section ${section.sectionKey} ${section.key}`, {
            'since-inception-disclaimers': this.hasSinceInceptionDisclaimers(section),
          })}
        >
          {this.renderSectionElement(section)}
          {section.disclaimerConfigs && section.disclaimerConfigs.length > 0 && (
            <div className={classNames('proposal-preview-disclaimer', { landscape: isLandscapeOrientation })}>
              {section.disclaimerConfigs &&
                section.disclaimerConfigs.map((disclaimerConfig, i) =>
                  this.renderDisclaimer(
                    disclaimerConfig,
                    section.showItemNamesInDisclaimers && proposal.items.length > 1,
                    i,
                    section.showDetailedDatesInDisclaimer,
                    section.hideSpecificDisclaimerKeys,
                    section,
                  ),
                )}
            </div>
          )}
        </div>
      );
    }

    return null;
  }
}

export { ProposalSectionComponent as UnwrappedProposalSectionComponent };

export default withFeatureFlags(ProposalSectionComponent);
